<template>
	<article class="chart_card">
		<!-- Sin título propio: el título colapsable del header del tab cubre
			las dos cards de la fila. La barra fina sólo lleva el toggle
			"% de ventas" (pesos ↔ share sobre las ventas de cada cabecera),
			superpuesto a la altura de la leyenda como el botón Comparación
			de la card de márgenes. -->
		<div class="chart_card_bar">
			<button type="button" class="action_btn"
				:class="{ 'is-active': percentMode }"
				:aria-pressed="String(percentMode)"
				:title="percentMode ? 'Ver montos' : 'Ver % de ventas'"
				@click="$emit('update:percent-mode', !percentMode)">
				% de ventas
			</button>
		</div>

		<div class="chart_canvas">
			<p v-if="error" class="chart_state chart_state--error">{{ error }}</p>
			<p v-else-if="isLoading" class="chart_state">Cargando…</p>
			<p v-else-if="!hasData" class="chart_state">
				Sin datos para el período seleccionado.
			</p>
			<KpiComposedChart v-else
				:monthHeaders="headers"
				:series="series" />
		</div>
	</article>
</template>

<script>
import KpiComposedChart from "@/components/financialReports/charts/KpiComposedChart.vue";

// Card del gráfico de costos y gastos del tab Rentabilidad (a la derecha del
// de Ventas & Márgenes): una línea por cuenta de COSTO del plan de cuentas
// más Gastos Totales, sobre las MISMAS filas y cabeceras que la tabla
// (`financialReports.tableRowHeaders` / getHeadersTable) — sin fetch propio.
// Las líneas salen de la estructura, no de una lista fija: Productos trae
// Costos de Productos / Variables / Fijos, Servicios sólo Costos Directos, y
// una línea de costo no canónica del plan también se dibuja. Recibe todo por
// props y no importa Vuex, como ProfitabilityChartCard.
//
// "% de ventas": cada línea pasa de pesos a costo / ventas netas × 100 de la
// misma cabecera (la fila "Gastos/Ventas %" de la tabla hace lo mismo). Una
// cabecera sin ventas viaja `null` — un 0 % dibujado mentiría. El redondeo a
// entero lo hace el formatter del chart. El modo lo gobierna el tab
// (`:percent-mode.sync`), como el toggle de SalesChartsRow.
const SALES_KEY = "ventas_netas";
const EXPENSES_KEY = "gastos_totales";
const COSTS_LEVEL_ONE = "Costos";
// Identidad por cuenta canónica (familia slate de Costos + ámbar de Gastos,
// tokens del tema rentabilidad). Productos y Directos comparten el primer
// tono: nunca conviven en un mismo plan canónico y ambos son "el" costo de
// vender. Una línea no canónica cae al neutro del tema.
const COST_COLOR_TOKENS = {
	costos_productos: "--report-chart-cost-1",
	costos_directos: "--report-chart-cost-1",
	costos_variables: "--report-chart-cost-2",
	costos_fijos: "--report-chart-cost-3",
	[EXPENSES_KEY]: "--report-chart-cost-4",
};
const OTHER_COLOR_TOKEN = "--report-chart-other";

function isNumber(value) {
	return typeof value === "number" && Number.isFinite(value);
}

export default {
	name: "ProfitabilityCostsChartCard",

	components: { KpiComposedChart },

	props: {
		// Cabeceras del rango activo (getHeadersTable), en orden cronológico.
		headers: { type: Array, default: () => [] },
		// `tableRowHeaders` del store: { [key]: { kind, levelOne, parentKey,
		// label, itemsDict: { [header]: n } } }. Las cuentas de costo presentes
		// definen qué líneas se dibujan.
		rows: { type: Object, default: () => ({}) },
		isLoading: { type: Boolean, default: false },
		error: { type: String, default: "" },
		// true → las líneas muestran el % sobre ventas en vez de pesos.
		percentMode: { type: Boolean, default: false },
	},

	computed: {
		salesValues() {
			return this.headers.map((header) => this.valueAt(SALES_KEY, header));
		},

		// Cuentas de costo de la estructura (nivel 2 del grupo Costos, sin
		// padre: los hijos colapsados de otros grupos no cuentan), en el orden
		// del store (el del plan), y Gastos Totales al final.
		lineRows() {
			const costs = Object.values(this.rows).filter((row) => (
				row && row.kind === "account"
				&& row.levelOne === COSTS_LEVEL_ONE
				&& !row.parentKey
			));
			const expenses = this.rows[EXPENSES_KEY];
			return expenses ? [...costs, expenses] : costs;
		},

		series() {
			const unit = this.percentMode ? "percent" : "money";
			return this.lineRows.map((row) => ({
				name: row.label || row.key,
				type: "line",
				unit,
				colorToken: COST_COLOR_TOKENS[row.key] || OTHER_COLOR_TOKEN,
				data: this.headers.map((header, i) => this.pointValue(row.key, header, i)),
			}));
		},

		hasData() {
			return this.headers.length > 0
				&& this.series.some((line) => line.data.some((value) => isNumber(value)));
		},
	},

	methods: {
		valueAt(key, header) {
			const row = this.rows[key];
			const value = row && row.itemsDict ? row.itemsDict[header] : undefined;
			return isNumber(value) ? value : null;
		},

		pointValue(key, header, index) {
			const value = this.valueAt(key, header);
			if (!this.percentMode) return value;
			const sales = this.salesValues[index];
			if (value == null || !sales) return null;
			return (value / sales) * 100;
		},
	},
};
</script>

<style scoped lang="scss">
// Misma chrome y mismas medidas que ProfitabilityChartCard: las dos cards
// comparten fila y tienen que alinear el canvas.
.chart_card {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 16px;
	box-shadow: var(--report-shadow-card);
	display: flex;
	flex-direction: column;
	overflow: hidden;
	padding: 10px 16px 16px;
	position: relative;
}

// El toggle se monta SOBRE la esquina del gráfico, a la altura de la leyenda
// que ECharts dibuja arriba a la izquierda (mismo `top` que el botón
// Comparación de la card vecina).
.chart_card_bar {
	position: absolute;
	top: 6px;
	right: 16px;
	z-index: 2;
	display: flex;
	justify-content: flex-end;
	align-items: center;
}

.action_btn {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 6px 12px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	display: flex;
	align-items: center;
	cursor: pointer;

	&:hover { background: var(--report-surface); }

	&:focus-visible {
		outline: 2px solid var(--report-accent-border);
		outline-offset: 2px;
	}

	&.is-active {
		background: var(--report-accent-bg);
		border-color: var(--report-accent-border);
		color: var(--report-accent-text);
	}
}

.chart_canvas {
	height: 300px;
	min-height: 0;
	display: flex;
	align-items: stretch;
	justify-content: stretch;
	position: relative;
}

.chart_state {
	margin: auto;
	color: var(--report-text-muted);
	font-size: 13px;
	text-align: center;

	&--error {
		color: var(--report-negative);
	}
}
</style>
